import React, { useEffect, useMemo, useState } from "react";
import { Checkbox } from "antd";
import styled from "styled-components";
import { ResponsiveAvatar } from "@/shared/components/Avatar/ResponsiveAvatar";
import { DynamicModal, modalScrollList, modalShrink } from "@/shared/components/Modal/DynamicModal";
import type { GroupApproval } from "@/api/creatorList";
import type { Creator, CreatorGroup } from "@/shared/types/campaign/creator";
import { formatNumberCommas } from "@/shared/utils/formatters";
import { normalizeEmail } from "@/shared/utils/email";

interface Props {
	open: boolean;
	group: CreatorGroup | null;
	approval: GroupApproval | null;
	campaignName?: string;
	currency: string;
	submitting: boolean;
	onCancel: () => void;
	onSubmit: (assignmentIds: string[]) => void;
}
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Subtitle = styled.div`
	color: #454545;
	font:
		400 16px/24px Poppins,
		sans-serif;
	span {
		color: #8a8a99;
		margin: 0 8px;
	}
`;
const Content = styled.div`
	${modalShrink}
	gap: 24px;
`;
const Banner = styled.div`
	padding: 14px 16px;
	border: 1px solid #b7e3c6;
	border-radius: 16px;
	background: #e9f8ef;
	color: #14532d;
`;
const BannerTitle = styled.div`
	font:
		600 14px/21px Poppins,
		sans-serif;
`;
const BannerText = styled.div`
	margin-top: 2px;
	color: #3f6b52;
	font:
		400 12px/18px Poppins,
		sans-serif;
`;
const SelectRow = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	color: #454545;
	font:
		500 14px/21px Poppins,
		sans-serif;
	.count {
		color: #8a8a99;
		font-weight: 400;
	}
`;
const List = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	${modalScrollList(267)}
`;
const CreatorRow = styled.label<{ $disabled: boolean }>`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px 16px;
	border: 1px solid ${(p) => (p.$disabled ? "#e4e4e4" : "#cdd4f6")};
	border-radius: 16px;
	background: ${(p) => (p.$disabled ? "#f7f7f7" : "#fff")};
	cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
	opacity: ${(p) => (p.$disabled ? 0.65 : 1)};
`;
const Identity = styled.div`
	min-width: 0;
`;
const Handle = styled.div`
	color: #303030;
	font:
		600 16px/24px Poppins,
		sans-serif;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;
const Meta = styled.div`
	color: #8a8a99;
	font:
		400 13px/20px Poppins,
		sans-serif;
`;
const MissingEmail = styled.div`
	color: #d30024;
	font:
		400 12px/18px Poppins,
		sans-serif;
`;
const getHandle = (creator: Creator) => {
	const account = creator.tiktok || creator.TikTok || creator.instagram || creator.Instagram;
	const handle = account?.platformUsername || account?.username || creator.name || "creator";
	return `@${String(handle).replace(/^@/, "")}`;
};

export const ApprovedGroupBatchModal: React.FC<Props> = ({
	open,
	group,
	approval,
	campaignName = "Campaign",
	currency,
	submitting,
	onCancel,
	onSubmit,
}) => {
	const eligible = useMemo(
		() =>
			(group?.creators || []).filter(
				(c) =>
					!c.addedToCampaign &&
					EMAIL_PATTERN.test(normalizeEmail(c.contactEmail || c.assignment?.contactEmail)),
			),
		[group],
	);
	const [selected, setSelected] = useState<string[]>([]);
	useEffect(() => {
		if (open) setSelected(eligible.map((c) => c.assignmentId).filter((id): id is string => Boolean(id)));
	}, [eligible, open]);
	if (!group || !approval) return null;
	const approvedDate = new Intl.DateTimeFormat("en", { month: "short", day: "numeric", year: "numeric" }).format(
		new Date(approval.createdAt),
	);
	const allSelected = eligible.length > 0 && selected.length === eligible.length;
	const toggle = (creator: Creator) => {
		if (
			!creator.assignmentId ||
			creator.addedToCampaign ||
			!EMAIL_PATTERN.test(normalizeEmail(creator.contactEmail || creator.assignment?.contactEmail))
		)
			return;
		setSelected((current) =>
			current.includes(creator.assignmentId!)
				? current.filter((id) => id !== creator.assignmentId)
				: [...current, creator.assignmentId!],
		);
	};
	return (
		<DynamicModal
			open={open}
			onCancel={onCancel}
			title="Add Creators in the campaign"
			description={
				<Subtitle>
					{campaignName}
					<span>•</span>
					<strong style={{ fontWeight: "500" }}>{group.name}</strong>
				</Subtitle>
			}
			confirmText={`Add ${selected.length} Creator${selected.length === 1 ? "" : "s"}`}
			cancelText="Cancel"
			onConfirm={() => onSubmit(selected)}
			confirmDisabled={selected.length === 0}
			loading={submitting}
			closable={!submitting}
			width={680}
			actionHeight={56}
		>
			<Content>
				<Banner>
					<BannerTitle>Client approved this group</BannerTitle>
					<BannerText>
						All {group.creators.length} creators in {group.name} were approved by{" "}
						{approval.authorName || "a client"} on {approvedDate}.
					</BannerText>
				</Banner>
				<SelectRow>
					<Checkbox
						checked={allSelected}
						indeterminate={selected.length > 0 && !allSelected}
						onChange={() =>
							setSelected(
								allSelected
									? []
									: eligible.map((c) => c.assignmentId).filter((id): id is string => Boolean(id)),
							)
						}
					>
						Select all approved creators
					</Checkbox>
					<span className="count">
						{selected.length} of {eligible.length} selected
					</span>
				</SelectRow>
				<List>
					{group.creators.map((creator) => {
						const email = normalizeEmail(creator.contactEmail || creator.assignment?.contactEmail);
						const disabled = creator.addedToCampaign || !EMAIL_PATTERN.test(email);
						const rate = creator.finalRate ?? creator.assignment?.finalRate;
						return (
							<CreatorRow
								key={creator.assignmentId || creator.id}
								$disabled={disabled}
								onClick={() => toggle(creator)}
							>
								<Checkbox
									checked={Boolean(creator.assignmentId && selected.includes(creator.assignmentId))}
									disabled={disabled}
									onClick={(event) => event.stopPropagation()}
									onChange={() => toggle(creator)}
								/>
								<ResponsiveAvatar
									displaySize={40}
									src={creator.img_url || "/default_avatar.png"}
									alt={creator.name}
									style={{ borderRadius: "50%", objectFit: "cover" }}
								/>
								<Identity>
									<Handle>{getHandle(creator)}</Handle>
									<Meta>
										{creator.tier || "—"}
										{creator.location && <>{"\u00a0•\u00a0" + creator.location}</>}
										{rate !== null && rate !== undefined && rate !== "" && (
											<>{`\u00a0•\u00a0Final rate ${currency} ${formatNumberCommas(rate)}`}</>
										)}
									</Meta>
									{creator.addedToCampaign ? (
										<MissingEmail>Already added</MissingEmail>
									) : !email ? (
										<MissingEmail>Add a valid campaign email to select this creator</MissingEmail>
									) : null}
								</Identity>
							</CreatorRow>
						);
					})}
				</List>
			</Content>
		</DynamicModal>
	);
};
